import { CartItemList } from '@/models/cart/cart-item'
import { basePriceToSelectNovelty, BundleApplied } from '@/usecases/novelty/base-price-to-select-novelty'
import _ from 'lodash'

import { ApiNoveltyCampaign, NoveltyRule } from '@/models/api/campaign/noveltyCampaign'
import { NoveltyCampaign } from '@/models/novelty/novelty'
import { FkuSummary } from './fku-summary'
import { NoveltySelection } from './novelty-selection'
import { SkuSummary } from './sku-summary'

export class CampaignSummary {
  constructor(
    private readonly noveltyCampaign: NoveltyCampaign,
    private readonly noveltySelections: NoveltySelection[],
    private readonly wholeCartItemList: CartItemList, // 特定のスコープのカート内商品全て・もしくは特定のスコープのカート内の単発注文商品全て
    private readonly bundleApplied: BundleApplied
    ) {
  }

  useNoveltiesAsSelection(novelties: NoveltySelection[]): CampaignSummary {
    return new CampaignSummary(
      this.noveltyCampaign,
      novelties,
      this.wholeCartItemList,
      this.bundleApplied
    )
  }

  get campaign(): ApiNoveltyCampaign {
    return this.noveltyCampaign.campaign
  }

  get basePriceToSelectNovelty(): number {
    return basePriceToSelectNovelty({
      wholeCartItemList: this.wholeCartItemList,
      novelty: this.noveltyCampaign.campaign,
      bundleApply: this.bundleApplied,
    })
  }

  get totalCount(): number {
    return _.sum(this.noveltySelections.map(s => s.count))
  }

  get maxCount(): number {
    return this.campaign.wholeSelectLimit
  }

  get requiringPrice(): number {
    return _.sum(
      this.noveltySelections.map((selection) => {
        const rule = this.getRuleForFku(selection.fkuId)

        if (!rule) {
          return 0
        }

        return rule.requiring_price * selection.count
      })
    )
  }

  get remainingPrice(): number {
    return this.basePriceToSelectNovelty - this.requiringPrice
  }

  get minimumRequiringPrice(): number {
    const minRule = _.minBy(this.allRules, rule => rule.requiring_price)

    if (!minRule) {
      return 0
    }

    return minRule.requiring_price
  }

  get allRules(): NoveltyRule[] {
    return this.campaign.rules
  }
  get allFkuSummaries(): FkuSummary[] {
    return this.campaign.rules.map((rule) => {
      const novelties = this.noveltyCampaign.noveltyList.filter(s => s.fkuId === rule.id)
      const noveltySelections = this.noveltySelections.filter(s => s.fkuId === rule.id)

      return new FkuSummary(rule, novelties, noveltySelections, this.basePriceToSelectNovelty)
    })
  }

  get allSkuFkuSummaries(): SkuSummary[] {
    return _.flatten(
      this.allFkuSummaries.map(summary => summary.allSkuSummaries)
    )
  }

  get userCanChangeCount(): boolean {
    return this.allSkuFkuSummaries.some(summary => summary.userCanChangeCount)
  }

  get cartPriceIsEnoughForSelectingSomeNovelty(): boolean {
    return this.minimumRequiringPrice <= this.basePriceToSelectNovelty
  }

  get isSomeSelected(): boolean {
    return this.totalCount > 0
  }

  get isNotYetSelected(): boolean {
    if (!this.userCanChangeCount) {
      // When there is no possibility of selecting a novelty, it is not considered as 'not-yet'.
      return false
    }

    return this.cartPriceIsEnoughForSelectingSomeNovelty && !this.isSomeSelected
  }

  private getRuleForFku(fkuId: string): NoveltyRule | null {
    return this.campaign.rules.find(rule => rule.id === fkuId) || null
  }
}
